import { createNodeIndex } from '../geometry/nodeIndex';
import { type Aabb2, type Point2, isSamePoint } from '../geometry/point';
import {
  polygonArea,
  polylineBounds,
  polylineLength,
} from '../geometry/polyline';
import {
  type ChunkGraph,
  type ChunkRef,
  chunkKey,
  createChunkGraph,
} from './chunkGraph';
import type { EdgeChunk, Loop, PolylineInput } from './types';

export type StitchOptions = {
  /**
   * How close two endpoints must be to count as the same point, in world units.
   *
   * DXF coordinates are rarely exact — a profile drawn as separate arcs and lines
   * routinely leaves gaps of 1e-9 or worse — so an exact match stitches almost
   * nothing. Callers should scale this to the drawing (a small fraction of its
   * diagonal) rather than pass an absolute figure.
   */
  readonly tolerance: number;
  /**
   * Whether a chain may run from one layer onto another. Defaults to `true`.
   *
   * Real drawings put a single profile across whatever layers the author happened
   * to be on, so layers cannot be trusted to bound a chain — a stitcher that
   * stopped at a layer change would shatter ordinary parts into fragments. The
   * cost is that a loop may span layers, so `Loop.layers` is a set and every
   * downstream rule (visibility, picking, painting) resolves at CHUNK granularity
   * rather than assuming a loop has one layer.
   */
  readonly acrossLayers?: boolean;
};

/** Endpoint slot: which end of which polyline. */
type EndRef = { edge: number; isStart: boolean };

/** The chunk an input became, and whether it runs against the input's own order. */
export type InputChunk = ChunkRef & { readonly isReversed: boolean };

const buildChunk = (
  index: number,
  input: PolylineInput,
  isReversed: boolean,
): EdgeChunk => {
  const points = isReversed ? [...input.points].reverse() : input.points;
  return {
    index,
    entityId: input.entityId,
    layer: input.layer,
    points,
    length: polylineLength(points),
    // A chunk always has at least two points by the time it gets here.
    bounds: polylineBounds(points) as Aabb2,
  };
};

const finishLoop = (
  id: number,
  chunks: EdgeChunk[],
  isClosed: boolean,
): Loop => {
  // Joins are shared points: each chunk after the first repeats its predecessor's
  // last point, and a closed loop repeats its first point at the very end.
  const points: Point2[] = [];
  chunks.forEach((chunk, chunkIndex) => {
    const from = chunkIndex === 0 ? 0 : 1;
    for (let index = from; index < chunk.points.length; index += 1) {
      points.push(chunk.points[index]);
    }
  });

  const areaPoints =
    isClosed && points.length > 1 ? points.slice(0, -1) : points;

  return {
    id,
    chunks,
    points,
    isClosed,
    length: chunks.reduce((total, chunk) => total + chunk.length, 0),
    area: isClosed ? polygonArea(areaPoints) : null,
    bounds: polylineBounds(points) as Aabb2,
    layers: new Set(chunks.map((chunk) => chunk.layer)),
  };
};

/**
 * Stitches entity geometry into loops.
 *
 * Endpoints are welded into shared nodes, then chains are walked outward from each
 * unvisited entity for as long as the node it arrives at joins exactly two
 * entities. Degree one ends the chain; **degree three or more also ends it**, so a
 * T-junction or a cross splits into separate loops rather than the walk having to
 * guess which branch continues the profile. Guessing is how a stitcher silently
 * swallows a whole feature into the wrong loop.
 *
 * An entity that is already closed on itself (a CIRCLE, or a closed LWPOLYLINE)
 * becomes a one-chunk closed loop without consulting its neighbours.
 *
 * Ids are assigned positionally and are only valid for this derivation.
 *
 * Returns the welded endpoint graph alongside the loops. It costs nothing —
 * the walk below builds it either way — and it is what lets a *later* pass
 * re-chain the same geometry under a rule this one knows nothing about, without
 * re-deriving the model and renumbering every loop id in the process.
 *
 * Also returns `inputChunks`: the chunk each input became, by input position,
 * so a caller can attach facts about its own source rows to the chunks they
 * made. Inputs with fewer than two points have no entry.
 */
export const stitchLoops = (
  inputs: readonly PolylineInput[],
  { tolerance, acrossLayers = true }: StitchOptions,
): {
  loops: Loop[];
  chunkGraph: ChunkGraph;
  inputChunks: readonly (InputChunk | undefined)[];
} => {
  const loops: Loop[] = [];
  const index = createNodeIndex(tolerance);

  const startNodes: number[] = [];
  const endNodes: number[] = [];
  const isConsumed: boolean[] = [];
  /** Where each input landed once the walk finished. */
  const chunkOfEdge: (InputChunk | undefined)[] = [];
  /** node → the endpoint slots that meet there. */
  const adjacency = new Map<number, EndRef[]>();

  const addAdjacency = (node: number, ref: EndRef) => {
    const existing = adjacency.get(node);
    if (existing) {
      existing.push(ref);
    } else {
      adjacency.set(node, [ref]);
    }
  };

  inputs.forEach((input, edge) => {
    startNodes.push(-1);
    endNodes.push(-1);
    isConsumed.push(false);

    if (input.points.length < 2) {
      isConsumed[edge] = true;
      return;
    }

    const first = input.points[0];
    const last = input.points[input.points.length - 1];
    if (!first || !last) {
      isConsumed[edge] = true;
      return;
    }

    if (isSamePoint(first, last, tolerance)) {
      // Self-closed: emit immediately and keep it out of the adjacency graph, so a
      // circle sitting tangent to a profile cannot be chained into it.
      isConsumed[edge] = true;
      chunkOfEdge[edge] = {
        loopId: loops.length,
        chunkIndex: 0,
        isReversed: false,
      };
      loops.push(finishLoop(loops.length, [buildChunk(0, input, false)], true));
      return;
    }

    const startNode = index.nodeFor(first);
    const endNode = index.nodeFor(last);
    startNodes[edge] = startNode;
    endNodes[edge] = endNode;
    addAdjacency(startNode, { edge, isStart: true });
    addAdjacency(endNode, { edge, isStart: false });
  });

  /**
   * The single other entity meeting at `node`, or `null` when the node is an end
   * (degree one) or a junction (degree three or more).
   */
  const continuationAt = (node: number, fromEdge: number): EndRef | null => {
    const refs = adjacency.get(node);
    if (!refs || refs.length !== 2) {
      return null;
    }
    const next = refs.find((ref) => ref.edge !== fromEdge);
    if (!next || isConsumed[next.edge]) {
      return null;
    }
    if (!acrossLayers && inputs[next.edge].layer !== inputs[fromEdge].layer) {
      return null;
    }
    return next;
  };

  inputs.forEach((_input, seed) => {
    if (isConsumed[seed]) {
      return;
    }

    isConsumed[seed] = true;
    const chain: { edge: number; isReversed: boolean }[] = [
      { edge: seed, isReversed: false },
    ];
    const seedStart = startNodes[seed];
    let isClosed = false;

    // Forward: keep arriving at a node and stepping onto the one other entity there.
    let node = endNodes[seed];
    let fromEdge = seed;
    for (;;) {
      if (node === seedStart) {
        isClosed = true;
        break;
      }
      const next = continuationAt(node, fromEdge);
      if (!next) {
        break;
      }
      isConsumed[next.edge] = true;
      // Arriving at the entity's *end* means traversing it backwards.
      chain.push({ edge: next.edge, isReversed: !next.isStart });
      node = next.isStart ? endNodes[next.edge] : startNodes[next.edge];
      fromEdge = next.edge;
    }

    // Backward from the seed, unless the chain already closed on itself.
    if (!isClosed) {
      node = seedStart;
      fromEdge = seed;
      for (;;) {
        const next = continuationAt(node, fromEdge);
        if (!next) {
          break;
        }
        isConsumed[next.edge] = true;
        // Prepending: arriving at the entity's *start* means it runs backwards.
        chain.unshift({ edge: next.edge, isReversed: next.isStart });
        node = next.isStart ? endNodes[next.edge] : startNodes[next.edge];
        fromEdge = next.edge;
      }
    }

    const chunks = chain.map(({ edge, isReversed }, chunkIndex) =>
      buildChunk(chunkIndex, inputs[edge], isReversed),
    );
    const loopId = loops.length;
    // Where each input ended up. Recorded here rather than recovered afterwards
    // from `entityId`, because one entity can appear as several separate inputs
    // and land in several different loops — the id alone cannot tell them apart.
    chain.forEach(({ edge, isReversed }, chunkIndex) => {
      chunkOfEdge[edge] = { loopId, chunkIndex, isReversed };
    });
    loops.push(finishLoop(loopId, chunks, isClosed));
  });

  // Adjacency, translated out of input-index space now that every input has an
  // address. Self-closed inputs never entered the graph and have no entry here.
  const ends = new Map<string, { start: number; end: number }>();
  const byNode = new Map<number, ChunkRef[]>();

  inputs.forEach((_input, edge) => {
    const landed = chunkOfEdge[edge];
    if (!landed || startNodes[edge] < 0) {
      return;
    }
    const chunk: ChunkRef = {
      loopId: landed.loopId,
      chunkIndex: landed.chunkIndex,
    };
    ends.set(chunkKey(chunk), {
      start: startNodes[edge],
      end: endNodes[edge],
    });
    [startNodes[edge], endNodes[edge]].forEach((node) => {
      const at = byNode.get(node);
      if (at) {
        at.push(chunk);
        return;
      }
      byNode.set(node, [chunk]);
    });
  });

  return {
    loops,
    chunkGraph: createChunkGraph({ ends, byNode }),
    inputChunks: chunkOfEdge,
  };
};
